import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatDate, formatMoney, toISODate } from '../lib/format';
import type { AccountType } from '../lib/types';

interface Row {
  code: string;
  name: string;
  type: AccountType;
  amount: number;
}

export default function ReportPL() {
  const { currentOrg } = useOrg();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!currentOrg) return;
    // Default = current financial year (SA: March → Feb).
    const m0 = currentOrg.financial_year_start_month - 1;
    const now = new Date();
    const startYear = now.getMonth() >= m0 ? now.getFullYear() : now.getFullYear() - 1;
    setFrom(toISODate(new Date(startYear, m0, 1)));
    setTo(toISODate(now));
  }, [currentOrg]);

  useEffect(() => { if (from && to && currentOrg) run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [from, to, currentOrg]);

  async function run() {
    if (!currentOrg) return;
    setBusy(true);
    const { data } = await supabase
      .from('dodobooks_journal_lines')
      .select(`
        debit, credit,
        account:dodobooks_accounts!inner(code, name, account_type, org_id),
        journal:dodobooks_journals!inner(date, org_id, is_posted)
      `)
      .eq('account.org_id', currentOrg.id)
      .eq('journal.org_id', currentOrg.id)
      .eq('journal.is_posted', true)
      .gte('journal.date', from)
      .lte('journal.date', to);

    const agg: Record<string, Row> = {};
    for (const r of (data ?? []) as any[]) {
      const a = r.account;
      if (!a) continue;
      if (!['revenue', 'cogs', 'expense'].includes(a.account_type)) continue;
      const key = a.code;
      if (!agg[key]) agg[key] = { code: a.code, name: a.name, type: a.account_type, amount: 0 };
      // Revenue: credit-debit; expense/COGS: debit-credit.
      const sign = a.account_type === 'revenue' ? 1 : -1;
      agg[key].amount += sign * (Number(r.credit) - Number(r.debit)) * (a.account_type === 'revenue' ? 1 : -1);
      // Simpler: revenue net = credit-debit; expense net = debit-credit.
      if (a.account_type === 'revenue') agg[key].amount = (agg[key].amount === 0 ? 0 : agg[key].amount); // already handled
    }
    // Recompute cleanly.
    const clean: Record<string, Row> = {};
    for (const r of (data ?? []) as any[]) {
      const a = r.account;
      if (!a) continue;
      if (!['revenue', 'cogs', 'expense'].includes(a.account_type)) continue;
      const key = a.code;
      if (!clean[key]) clean[key] = { code: a.code, name: a.name, type: a.account_type, amount: 0 };
      if (a.account_type === 'revenue') clean[key].amount += Number(r.credit) - Number(r.debit);
      else clean[key].amount += Number(r.debit) - Number(r.credit);
    }
    setRows(Object.values(clean).sort((a, b) => a.code.localeCompare(b.code)));
    setBusy(false);
  }

  const revenue = rows.filter((r) => r.type === 'revenue');
  const cogs = rows.filter((r) => r.type === 'cogs');
  const expenses = rows.filter((r) => r.type === 'expense');
  const revTotal = revenue.reduce((s, r) => s + r.amount, 0);
  const cogsTotal = cogs.reduce((s, r) => s + r.amount, 0);
  const expTotal = expenses.reduce((s, r) => s + r.amount, 0);
  const grossProfit = revTotal - cogsTotal;
  const netProfit = grossProfit - expTotal;

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="h-page">Profit & loss</h1>
        <p className="text-text-dim text-sm mt-1">Income statement for the chosen period.</p>
      </header>

      <div className="card grid grid-cols-2 gap-4 max-w-md">
        <div className="field"><label>From</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div className="field"><label>To</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="h-section">{currentOrg?.name}</h2>
          <span className="text-xs text-text-mute font-mono">{formatDate(from)} → {formatDate(to)}</span>
        </div>

        {busy ? (
          <div className="text-text-mute text-sm py-8 text-center">Calculating…</div>
        ) : (
          <table>
            <tbody>
              <tr><td colSpan={2} className="font-serif text-base font-semibold pt-2">Revenue</td></tr>
              {revenue.map((r) => (
                <tr key={r.code}><td className="pl-4 text-text-dim">{r.code} · {r.name}</td><td className="text-right num">{formatMoney(r.amount)}</td></tr>
              ))}
              <tr><td className="pl-4 font-serif font-semibold">Total revenue</td><td className="text-right num text-emerald font-semibold">{formatMoney(revTotal)}</td></tr>

              {cogs.length > 0 && (
                <>
                  <tr><td colSpan={2} className="font-serif text-base font-semibold pt-4">Cost of sales</td></tr>
                  {cogs.map((r) => (
                    <tr key={r.code}><td className="pl-4 text-text-dim">{r.code} · {r.name}</td><td className="text-right num">{formatMoney(r.amount)}</td></tr>
                  ))}
                  <tr><td className="pl-4 font-serif font-semibold">Total cost of sales</td><td className="text-right num text-rust">{formatMoney(cogsTotal)}</td></tr>
                  <tr><td className="pl-4 font-serif font-semibold">Gross profit</td><td className="text-right num text-emerald font-semibold">{formatMoney(grossProfit)}</td></tr>
                </>
              )}

              <tr><td colSpan={2} className="font-serif text-base font-semibold pt-4">Expenses</td></tr>
              {expenses.map((r) => (
                <tr key={r.code}><td className="pl-4 text-text-dim">{r.code} · {r.name}</td><td className="text-right num">{formatMoney(r.amount)}</td></tr>
              ))}
              <tr><td className="pl-4 font-serif font-semibold">Total expenses</td><td className="text-right num text-rust">{formatMoney(expTotal)}</td></tr>

              <tr><td colSpan={2} className="border-t-2 border-emerald/30 pt-4 mt-4"></td></tr>
              <tr>
                <td className="font-serif text-lg font-semibold">Net profit</td>
                <td className={`text-right num font-serif text-lg font-semibold ${netProfit >= 0 ? 'text-emerald' : 'text-rust'}`}>
                  {formatMoney(netProfit)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
